import { palette } from 'src/styles/colors';
import styled, { css } from 'styled-components';

const background = css`
  background: rgba(28, 171, 191, 0.13);
  padding: 50px 15px;
  display: grid;
  justify-self: center;
`

const h2 = css`
  font-weight: 900;
  font-size: 50px;
  max-width: 800px;
  line-height: 57px;
  justify-self: center;
  text-align: center;

  @media only screen and (max-width: 700px) {
    font-size: 32px;
    line-height: 110%;
    padding: 0px 25px;
  }
`

// -------------------------------
// ------ PARENT CONTAINER -------

// 100vw !important is needed to override the section layout of 1440px
const CarouselBackground = styled.div`
  max-width: 100vw !important;
  padding: 3em 0px;
  background: rgba(28, 171, 191, 0.08);
  & > div {
    max-width: 1440px;
    margin: 0 auto;
  }
`

const CarouselAlignment = styled.div`
display: grid;
& > h1 {
  width: 869px;
  max-width: 90vw;
  justify-self: center;
  @media all and (max-width: 700px) {
    max-width: 80vw;
  }
}
`

const CarouselWrapper = styled.div`
  display: grid;
  max-width: 1440px;
  width: 100%;
  justify-self: center;
  position: relative;
  grid-template-columns: 0px 1fr 0px;

  @media only screen and (max-width: 700px) {
    grid-template-columns: 1fr;
    overflow: hidden;
  }
`

interface ChevronWrapperProps {
  orientation: 'left' | 'right';
  hideOnSm: boolean;
  chevronColor?: string;
}

// >> Parent of chevronLeft and chevronRight
const ChevronWrapper = styled.div<ChevronWrapperProps>`
  align-self: center;
  justify-self: center;
  position: relative;
  z-index: 4;
  cursor: pointer;
  ${({ orientation }) => orientation}: 34px;

  ${({ chevronColor }) => !chevronColor ? '' : css`
    > svg > path {
      fill: ${chevronColor};
    }
  `}

  @media only screen and (max-width: 700px) {
    display: none;

    position: absolute;
    width: 50%;
    height: 100%;
    ${({ hideOnSm }) => hideOnSm ? 'display: none' : ''};
  }
`

const CarouselContainer = styled.div`
  position: relative;
  height: 743px;
  perspective: 800px;
  max-width: 1440px;
  width: 100%;
  justify-self: center;

  @media only screen and (max-width: 700px) {
    height: auto;
  }
`

// -------------------------------
// ----- HORIZONTAL VIEW -------

const CarouselSlider = styled.div`
  display: grid;
  column-gap: 4px;
  max-width: 100vw;
  left: calc(50% - 135px);
  position: relative;
  grid-template-columns: repeat(5, 1fr);
  transition: transform .2s ease-out;
`

const CarouselSliderElement = styled.div`
  transform-origin: center;
  transition: all ease-in-out 210ms;
  margin-left: -25px;
  position: relative;
  width: 270px;

  &:first-child {
    margin-left: 0px;
  }
`

const CarouselSliderElementImage = styled.img`
  max-height: 488px;
  max-width: 270px;
`

const CarouselElement = styled.div`
  position: absolute;
  left: 0px;
  top: 0px;
  width: 411px;
  transform-origin: center;
  transition: all ease-in-out 210ms;
`

const CarouselImage = styled.img`
  max-height: 743px;
  max-width: 411px;
`

// ------------------------------
// ----- TEXT BLOCK STYLES ------

const CarouselTextContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
`

const CarouselTextElement = styled.div<{visible: boolean}>`
  display: grid;
  justify-self: center;
  grid-column: 1 / 2;
  grid-row: 1 / 2;
  row-gap: 12px;
  opacity: ${({ visible }) => visible ? 1 : 0};
  transition: all ease-in-out 250ms;
  p {
    width: 340px;
  }
`

const h4 = css`
  font-weight: normal;
  font-size: 24px;
  line-height: 29px;
  text-align: center;
  justify-self: center;
  margin-bottom: 17px;

  @media only screen and (max-width: 700px) {
    font-size: 24px;
    line-height: 26px;
  }
`

const paragraph = css`
  width: 350px;
  font-style: normal;
  font-weight: 300;
  font-size: 22px;
  line-height: 26px;
  text-align: center;
  justify-self: center;

  @media only screen and (max-width: 700px) {
    font-size: 18px;
    line-height: 22px;
  }
  @media only screen and (max-width: 500px) {
    font-size: 28px;
    line-height: 110%;
    padding: 0px 25px;
  }
`

const MobileDotsPhoneCarouselContainer = styled.div`
  max-width: 400px !important;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  padding: 0;
`

const MobileDotsPhoneCarousel = styled.div<{ isActive: boolean; }>`
  width: 20px;
  height: 20px;
  position: relative;
  display: inline-block;
  margin: 5px;
  padding: 0;
  cursor: pointer;
  color: transparent;
  border: 0;
  outline: none;
  background: transparent;

  &:after {
    content: "";
    position: absolute;
    width: 10px;
    height: 10px;
    left: calc(50% - 5px);
    top: calc(50% - 5px);
    border-radius: 50%;
    outline: none;

    ${({ isActive }) => isActive ? css`
      background-color: #FF00FF;
      opacity: 0.9;
    ` : css`
      background-color: ${palette.black};
      opacity: 0.7;
    `}
  }
`

// ------------------------------
// ---- ACTION BUTTON STYLES ----

const CarouselButtonContainer = styled.div`
  display: grid;
  row-gap: 2em;
  margin-top: 1rem;

  @media all and (max-width: 700px) {
    margin-top: 1.5rem;
  }
`
// ------------------------------

export {
  h2,
  background,
  CarouselBackground,
  CarouselAlignment,
  ChevronWrapper,
  CarouselWrapper,
  CarouselContainer,
  CarouselSlider,
  CarouselSliderElement,
  CarouselSliderElementImage,
  CarouselElement,
  CarouselImage,
  CarouselButtonContainer,
  CarouselTextContainer,
  CarouselTextElement,
  h4,
  paragraph,
  MobileDotsPhoneCarouselContainer,
  MobileDotsPhoneCarousel
}
